import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PublicHeader } from "@/components/PublicHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { AuthField, SubmitButton } from "@/components/auth/AuthLayout";
import { ProfileFields, fromMember, profileError, useMemberForm } from "@/components/member/ProfileFields";
import { SessionsCard } from "@/components/auth/SessionsCard";
import { useApp } from "@/context/AppContext";
import { api, errMsg, showPin } from "@/lib/api";

export default function MemberProfile() {
  const { auth, member, signIn, loggedOut } = useApp();
  const [params] = useSearchParams();
  const next = params.get("next") || "/my";
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const form = useMemberForm();
  const nav = useNavigate();
  useEffect(() => {
    if (member) { setP((cur) => cur || fromMember(member)); }
  }, [member]);
  if (auth === null) return <div className="min-h-screen grid place-items-center text-gray-500">Loading…</div>;
  if (!member) return <Navigate to={loggedOut.current ? "/" : "/login"} replace />;
  if (!member.email_verified) return <Navigate to={`/member/verify?next=${encodeURIComponent("/my/profile")}`} replace />;
  const incomplete = !member.profile_complete;

  const save = async (e) => {
    e.preventDefault();
    const err = profileError(p, form);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const r = await api.put("/members/me/profile", p);
      signIn(r.data);
      toast.success("Profile saved");
      nav(next);
    } catch (ex) { toast.error(errMsg(ex)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="member-profile-page">
      <PublicHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E60000]">My EventHub</div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-1">{incomplete ? "Complete your profile" : "My profile"}</h1>
        <p className="text-sm text-gray-500 mt-1">{incomplete ? "We need a few more details before you can register for events." : "Keep your details up to date. They are used on your badge, receipts and certificates."}</p>
        {incomplete && <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="profile-incomplete-banner">Please fill in all required fields (*) and save to continue.</div>}
        {p && form && (
          <form onSubmit={save} noValidate className="mt-6 bg-white border border-[#E5E7EB] rounded-lg p-5 sm:p-8 space-y-4" data-testid="member-profile-form">
            <ProfileFields form={form} p={p} set={(x) => setP((s) => ({ ...s, ...x }))} slots={{
              pin: (label) => <AuthField label={label.replace(" *", "")}><Input value={showPin(member.pin)} disabled className="h-11 font-mono" data-testid="profile-pin-readonly" /></AuthField>,
              email: (label) => <AuthField label={label.replace(" *", "")}><Input value={member.email} disabled className="h-11" data-testid="profile-email-readonly" /></AuthField>,
              gamls_id: (label) => <AuthField label={label.replace(" *", "")}><Input value={member.gamls_id || "Not provided"} disabled className="h-11 font-mono" data-testid="profile-gamls-id-readonly" /></AuthField>,
              staff_id: (label) => <AuthField label={label.replace(" *", "")}><Input value={member.staff_id || "Not provided"} disabled className="h-11 font-mono" data-testid="profile-staff-id-readonly" /></AuthField>,
            }} />
            <p className="text-xs text-gray-500">To change your AHPC PIN, email address, GAMLS ID or Staff ID, please contact the GAMLS secretariat via Contact Us.</p>
            <div className="sm:w-64 sm:ml-auto"><SubmitButton busy={busy} testId="profile-save-button">{busy ? "Saving…" : incomplete ? "Save and continue" : "Save changes"}</SubmitButton></div>
            {!incomplete && <Link to="/my" className="block text-center text-sm text-gray-500 hover:text-[#E60000]" data-testid="profile-cancel">Back to My EventHub</Link>}
          </form>
        )}
        {!incomplete && (
          <div className="mt-6 space-y-4" data-testid="profile-security-section">
            <SessionsCard wide />
            <div className="rounded-lg border border-[#E5E7EB] bg-white p-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="flex-1 text-sm text-gray-500">Changing your password also signs you out on every other device.</p>
              <Link to="/change-password" className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-[#111] text-sm font-semibold hover:bg-[#111] hover:text-white transition-colors" data-testid="profile-change-password-link">Change password</Link>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
